import {
  DARK_MODE_COLOR,
  DARK_TRAINING_DOT_COLORS,
  getScaledLabelFont,
  LIGHT_MODE_COLOR,
  LIGHT_TRAINING_DOT_COLORS,
  SVG_FALLBACK_CROSSHAIR_COLOR,
} from 'enums/canvas';
import { CROSS2_SVG, CROSSHAIR_SVG } from 'enums/svg-xmls';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { RAD_FULL_ROTATION } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { IBreakConfig } from 'lib_ts/interfaces/i-break-config';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';

// magic numbers need to be rescaled by HEIGHT_PX divided by REF_HEIGHT_PX (e.g. 800 / 400 = 2x)
const REF_HEIGHT_PX = 400;

const BASE_RULER_FONT_SIZE = 12;

export class BreakCanvas {
  // fallback to empty breaks
  static getMedian(breaks: ITrajektRefBreak[]): ITrajektRefBreak {
    if (breaks.length === 0) {
      return {
        xInches: 0,
        zInches: 0,
      };
    }

    return MiscHelper.getMedianObject(breaks) as ITrajektRefBreak;
  }

  static makeSquare(isDark: boolean) {
    return new BreakCanvas({
      isDark: isDark,

      aspectRatio: 1,
      height_px: 800,

      // controls the actual bounds of the canvas
      min_in: -30,
      max_in: 30,

      // controls where the lines and tick marks stop
      min_axis_in: -22,
      max_axis_in: 22,
    });
  }

  static makeBigSquare(isDark: boolean) {
    return new BreakCanvas({
      isDark: isDark,

      aspectRatio: 1,
      height_px: 1200,

      // controls the actual bounds of the canvas
      min_in: -30,
      max_in: 30,

      // controls where the lines and tick marks stop
      min_axis_in: -30,
      max_axis_in: 30,
    });
  }

  readonly CONFIG: IBreakConfig;

  private readonly CONSTANTS_PX: {
    origin_x: number;
    origin_y: number;
  };

  constructor(config: {
    isDark: boolean;

    aspectRatio: number;
    height_px: number;

    min_in: number;
    max_in: number;

    min_axis_in: number;
    max_axis_in: number;

    step?: number;
  }) {
    const total_y_in = config.max_in - config.min_in;
    const abs_x_in = (total_y_in * config.aspectRatio) / 2;

    this.CONFIG = {
      isDark: config.isDark,
      main_color: config.isDark ? DARK_MODE_COLOR : LIGHT_MODE_COLOR,
      canvas: {
        width_px: config.height_px * config.aspectRatio,
        height_px: config.height_px,
        scale: config.height_px / REF_HEIGHT_PX,
      },
      x: {
        min_in: -abs_x_in,
        max_in: abs_x_in,

        min_axis_in: config.min_axis_in,
        max_axis_in: config.max_axis_in,

        step: config.step ?? 0.00001,
      },
      y: {
        min_in: config.min_in,
        max_in: config.max_in,

        min_axis_in: config.min_axis_in,
        max_axis_in: config.max_axis_in,

        step: config.step ?? 0.00001,
      },
    };

    /** must be after plate is defined */
    this.CONSTANTS_PX = {
      origin_x: this.inchToPxX(0),
      origin_y: this.inchToPxY(0),
    };
  }

  private inchToPxY(y_ft: number) {
    /** convert y_ft into a px value that works for the canvas by translating so that y.min_ft is the same as 0 for the canvas */
    const total_ft = Math.abs(this.CONFIG.y.max_in - this.CONFIG.y.min_in);
    const y_perc = (y_ft - this.CONFIG.y.min_in) / total_ft;

    /** canvas y is inverted relative to slider */
    return this.CONFIG.canvas.height_px * (1 - y_perc);
  }

  private inchToPxX(x_ft: number) {
    /** convert x_ft into a px value that works for the canvas by translating so that x.min_ft is the same as 0 for the canvas */
    const total_ft = Math.abs(this.CONFIG.x.max_in - this.CONFIG.x.min_in);
    const x_perc = (x_ft - this.CONFIG.x.min_in) / total_ft;
    return this.CONFIG.canvas.width_px * x_perc;
  }

  drawRulersForEditBreaks(
    ctx: CanvasRenderingContext2D,
    tickIntervalIn?: number
  ) {
    const scale = this.CONFIG.canvas.scale;
    const safeTickIntervalIn = tickIntervalIn ?? 5;

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.scale,
      BASE_RULER_FONT_SIZE
    );

    ctx.lineWidth = 2 * this.CONFIG.canvas.scale;
    ctx.strokeStyle = this.CONFIG.main_color;
    ctx.fillStyle = this.CONFIG.main_color;

    /** draw x ruler */
    const x_ruler_pos_y = Math.min(
      this.CONSTANTS_PX.origin_y,
      this.inchToPxY(this.CONFIG.y.min_axis_in)
    );
    ctx.moveTo(this.inchToPxY(this.CONFIG.x.min_axis_in), x_ruler_pos_y);
    ctx.lineTo(this.inchToPxY(this.CONFIG.x.max_axis_in), x_ruler_pos_y);
    ctx.stroke();

    const minX = Math.ceil(this.CONFIG.x.min_axis_in);
    const maxX = Math.floor(this.CONFIG.x.max_axis_in);

    /** draw x ruler tick marks + units per foot */
    ArrayHelper.getIntegerOptions(minX, maxX).forEach((o) => {
      const intValue = parseInt(o.value);

      if (intValue % safeTickIntervalIn !== 0) {
        return;
      }

      const x = this.inchToPxX(intValue);
      ctx.moveTo(x, x_ruler_pos_y);
      ctx.lineTo(x, x_ruler_pos_y - 5 * scale);
      ctx.stroke();

      if (intValue !== 0) {
        ctx.fillText(
          `${o.value}"`,
          x - (intValue > 0 ? 6 : 9) * scale,
          x_ruler_pos_y - 10 * scale
        );
      }
    });

    /** draw y ruler down the middle */
    const y_ruler_pos_x = Math.min(
      this.CONSTANTS_PX.origin_x,
      this.inchToPxY(this.CONFIG.x.min_axis_in)
    );

    ctx.moveTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.min_axis_in));
    ctx.lineTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.max_axis_in));
    ctx.stroke();

    const minY = Math.ceil(this.CONFIG.y.min_axis_in);
    const maxY = Math.floor(this.CONFIG.y.max_axis_in);

    /** draw y ruler tick marks + units per foot */
    ArrayHelper.getIntegerOptions(minY, maxY).forEach((o) => {
      const intValue = parseInt(o.value);

      if (intValue % safeTickIntervalIn !== 0) {
        return;
      }

      const y = this.inchToPxY(intValue);
      ctx.moveTo(y_ruler_pos_x, y);
      ctx.lineTo(y_ruler_pos_x + 5 * scale, y);
      ctx.stroke();

      if (intValue !== 0) {
        ctx.fillText(`${intValue}"`, y_ruler_pos_x + 8 * scale, y + 4 * scale);
      }
    });
  }

  // only draws min/max units + no tick marks
  drawRulersForPitchPerformance(
    ctx: CanvasRenderingContext2D,
    tickIntervalIn?: number
  ) {
    const scale = this.CONFIG.canvas.scale;
    const safeTickIntervalIn = tickIntervalIn ?? 5;

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.scale,
      BASE_RULER_FONT_SIZE
    );

    ctx.lineWidth = 2 * this.CONFIG.canvas.scale;
    ctx.strokeStyle = this.CONFIG.main_color;
    ctx.fillStyle = this.CONFIG.main_color;

    /** draw x ruler */
    const x_ruler_pos_y = Math.min(
      this.CONSTANTS_PX.origin_y,
      this.inchToPxY(this.CONFIG.y.min_axis_in)
    );
    ctx.moveTo(this.inchToPxY(this.CONFIG.x.min_axis_in), x_ruler_pos_y);
    ctx.lineTo(this.inchToPxY(this.CONFIG.x.max_axis_in), x_ruler_pos_y);
    ctx.stroke();

    const minX = Math.ceil(this.CONFIG.x.min_axis_in);
    const maxX = Math.floor(this.CONFIG.x.max_axis_in);

    /** draw x min and max units only (below ruler line) */
    [minX, maxX].forEach((o, i) => {
      if (o % safeTickIntervalIn !== 0) {
        return;
      }

      const x = this.inchToPxX(o);

      if (i === 0) {
        ctx.fillText(`${o}"`, x + 4 * scale, x_ruler_pos_y + 16 * scale);
      } else {
        ctx.fillText(`${o}"`, x - 22 * scale, x_ruler_pos_y + 16 * scale);
      }
    });

    /** draw y ruler down the middle */
    const y_ruler_pos_x = Math.min(
      this.CONSTANTS_PX.origin_x,
      this.inchToPxY(this.CONFIG.x.min_axis_in)
    );

    ctx.moveTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.min_axis_in));
    ctx.lineTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.max_axis_in));
    ctx.stroke();

    const minY = Math.ceil(this.CONFIG.y.min_axis_in);
    const maxY = Math.floor(this.CONFIG.y.max_axis_in);

    /** draw y min and max units only, without tick marks */
    [minY, maxY].forEach((v, i) => {
      if (v % safeTickIntervalIn !== 0) {
        return;
      }

      const y = this.inchToPxY(v);

      if (i === 0) {
        ctx.fillText(`${v}"`, y_ruler_pos_x - 28 * scale, y - 8 * scale);
      } else {
        ctx.fillText(`${v}"`, y_ruler_pos_x - 24 * scale, y + 14 * scale);
      }
    });
  }

  drawGrid(
    ctx: CanvasRenderingContext2D,
    tickIntervalIn?: number,
    // minimal - skip drawing outside lines
    minimal?: boolean
  ) {
    const safeTickIntervalIn = tickIntervalIn ?? 5;

    ctx.lineWidth = 1 * this.CONFIG.canvas.scale;
    ctx.strokeStyle = this.CONFIG.main_color;
    ctx.fillStyle = this.CONFIG.main_color;

    const minX = this.inchToPxX(this.CONFIG.x.min_axis_in);
    const maxX = this.inchToPxX(this.CONFIG.x.max_axis_in);
    // y is flipped
    const minY = this.inchToPxY(this.CONFIG.y.max_axis_in);
    const maxY = this.inchToPxY(this.CONFIG.y.min_axis_in);

    const xIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil(this.CONFIG.x.min_axis_in),
      Math.floor(this.CONFIG.x.max_axis_in)
    );

    /** draw x lines per interval */
    xIntegerOptions.forEach((o, i) => {
      if (minimal && (i === 0 || i === xIntegerOptions.length - 1)) {
        // don't draw first and last lines
        return;
      }

      const intValue = parseInt(o.value);

      if (intValue % safeTickIntervalIn !== 0) {
        return;
      }

      const x = this.inchToPxX(intValue);
      ctx.moveTo(x, minY);
      ctx.lineTo(x, maxY);
      ctx.stroke();
    });

    const yIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil(this.CONFIG.y.min_axis_in),
      Math.floor(this.CONFIG.y.max_axis_in)
    );

    /** draw y lines per interval */
    yIntegerOptions.forEach((o, i) => {
      if (minimal && (i === 0 || i === yIntegerOptions.length - 1)) {
        // don't draw first and last lines
        return;
      }

      const intValue = parseInt(o.value);

      if (intValue % safeTickIntervalIn !== 0) {
        return;
      }

      const y = this.inchToPxY(intValue);
      ctx.moveTo(minX, y);
      ctx.lineTo(maxX, y);
      ctx.stroke();
    });
  }

  drawCrosshairs(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      width_px: number;
      color?: string;
      beforeDraw?: () => void;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = config.width_px * this.CONFIG.canvas.scale;

      const img = new Image(scaledSize, scaledSize);

      const svgXml = CROSSHAIR_SVG.replaceAll(
        'currentColor',
        // default is to replace the white with red
        config.color ?? SVG_FALLBACK_CROSSHAIR_COLOR
      );

      img.src = 'data:image/svg+xml;charset=utf-8,' + svgXml;

      img.onload = () => {
        const x = this.inchToPxX(config.value.xInches);
        const y = this.inchToPxY(config.value.zInches);
        const half = scaledSize / 2;

        // img.onload is async, so we may want to do something right before drawing (e.g. clear canvas)
        if (config.beforeDraw) {
          config.beforeDraw();
        }

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawCross2(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      width_px: number;
      color?: string;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = config.width_px * this.CONFIG.canvas.scale;

      const img = new Image(scaledSize, scaledSize);

      const svgXml = CROSS2_SVG.replaceAll(
        'currentColor',
        // default is to replace the white with red
        config.color ?? SVG_FALLBACK_CROSSHAIR_COLOR
      );

      img.src = 'data:image/svg+xml;charset=utf-8,' + svgXml;

      img.onload = () => {
        const x = this.inchToPxX(config.value.xInches);
        const y = this.inchToPxY(config.value.zInches);
        const half = scaledSize / 2;

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawTrainingBreak(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      isNew: boolean;
      size: number;
      index: number;
    }
  ) {
    const scaledSize = config.size * this.CONFIG.canvas.scale;

    if (
      config.value.xInches < this.CONFIG.x.min_in ||
      config.value.xInches > this.CONFIG.x.max_in
    ) {
      return;
    }

    if (
      config.value.zInches < this.CONFIG.y.min_in ||
      config.value.zInches > this.CONFIG.y.max_in
    ) {
      return;
    }

    const dots = this.CONFIG.isDark
      ? DARK_TRAINING_DOT_COLORS
      : LIGHT_TRAINING_DOT_COLORS;

    const colors = config.isNew ? dots.new : dots.old;

    ctx.lineWidth = 2 * this.CONFIG.canvas.scale;
    ctx.fillStyle = colors.background;
    ctx.strokeStyle = colors.border;

    ctx.beginPath();
    ctx.ellipse(
      this.inchToPxX(config.value.xInches),
      this.inchToPxY(config.value.zInches),
      scaledSize,
      scaledSize,
      0,
      0,
      RAD_FULL_ROTATION
    );

    ctx.fill();
    ctx.stroke();

    ctx.font = getScaledLabelFont(this.CONFIG.canvas.scale);
    ctx.fillStyle = colors.label;

    // inside the circle
    ctx.fillText(
      config.index.toString(),
      this.inchToPxX(config.value.xInches) -
        scaledSize / 2 +
        1 * this.CONFIG.canvas.scale,
      this.inchToPxY(config.value.zInches) + scaledSize / 2
    );
  }

  drawBreak(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      // radius of dot in inches
      radiusIn: number;
      // e.g. hex, rgba
      fillColor?: string;
      borderColor?: string;
      borderWidth?: number;
    }
  ) {
    const radiusPx = this.inchToPxX(config.radiusIn) - this.inchToPxX(0);

    if (
      config.value.xInches < this.CONFIG.x.min_in ||
      config.value.xInches > this.CONFIG.x.max_in
    ) {
      return;
    }

    if (
      config.value.zInches < this.CONFIG.y.min_in ||
      config.value.zInches > this.CONFIG.y.max_in
    ) {
      return;
    }

    // fallback is success-9
    ctx.fillStyle = config.fillColor ?? '#30A46C';

    ctx.beginPath();
    ctx.ellipse(
      this.inchToPxX(config.value.xInches),
      this.inchToPxY(config.value.zInches),
      radiusPx,
      radiusPx,
      0,
      0,
      RAD_FULL_ROTATION
    );

    ctx.fill();

    if (config.borderColor && config.borderWidth) {
      ctx.setLineDash([]);
      ctx.strokeStyle = config.borderColor;
      ctx.lineWidth = config.borderWidth;
      ctx.stroke();
    }

    ctx.closePath();
  }

  drawBreakCircle(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      // radius of circle in inches
      radiusIn: number;
      borderColor: string;
      borderWidth: number;
    }
  ) {
    const radiusPx = this.inchToPxX(config.radiusIn) - this.inchToPxX(0);

    ctx.beginPath();
    ctx.ellipse(
      this.inchToPxX(config.value.xInches),
      this.inchToPxY(config.value.zInches),
      radiusPx,
      radiusPx,
      0,
      0,
      RAD_FULL_ROTATION
    );

    ctx.setLineDash([]);
    ctx.strokeStyle = config.borderColor;
    ctx.lineWidth = config.borderWidth;
    ctx.stroke();

    ctx.closePath();
  }

  // label will be positioned near the value coordinates
  // showing the deltas of subtracting the target from the value
  drawLabel(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      target: ITrajektRefBreak;
      // e.g. hex, rgba
      fontColor: string;
    }
  ) {
    const roundTo1 = (v: number) => Math.round(v * 10) / 10;

    // label the point
    const delta_x_in = roundTo1(config.value.xInches - config.target.xInches);
    const delta_z_in = roundTo1(config.value.zInches - config.target.zInches);

    const formatLabel = (v: number) =>
      `${v > 0 ? '+' : v < 0 ? '-' : ''}${Math.abs(v).toFixed(1)}`;

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.scale,
      BASE_RULER_FONT_SIZE
    );
    ctx.fillStyle = config.fontColor;

    ctx.fillText(
      `${formatLabel(delta_x_in)}", ${formatLabel(delta_z_in)}"`,
      this.inchToPxX(config.value.xInches) + 16,
      this.inchToPxY(config.value.zInches) + 16
    );
  }

  drawLine(
    ctx: CanvasRenderingContext2D,
    config: {
      from: ITrajektRefBreak;
      to: ITrajektRefBreak;
      lineColor: string;
    }
  ) {
    // draw the line to the target under the dot
    ctx.strokeStyle = config.lineColor;
    ctx.lineWidth = 1 * this.CONFIG.canvas.scale; // line thickness

    ctx.setLineDash([8, 4]);

    ctx.beginPath();
    ctx.moveTo(
      this.inchToPxX(config.from.xInches),
      this.inchToPxY(config.from.zInches)
    );
    ctx.lineTo(
      this.inchToPxX(config.to.xInches),
      this.inchToPxY(config.to.zInches)
    );
    ctx.stroke();
    ctx.closePath();
  }
}
